import { Linkedin, Twitter, Github } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-slate-950 pt-24 pb-12 overflow-hidden border-t border-white/5">
      {/* Background Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[400px] bg-emerald-500/5 blur-[120px] pointer-events-none"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-20">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-8 group" aria-label="GreenTick Home">
              <picture>
                <source srcSet="/image_40.webp" type="image/webp" />
                <img 
                  src="/image.png" 
                  alt="GreenTick AI" 
                  width="40"
                  height="40"
                  className="h-10 group-hover:opacity-80 transition-opacity" 
                />
              </picture>
            </Link>
            <p className="text-slate-400 mb-8 max-w-sm leading-relaxed text-sm italic font-medium">
              GreenTick is the world's first <span className="text-teal-400">Human Potential Capture</span> ecosystem for hiring assessments. We move beyond static screening to map senior-level mental models and multi-step reasoning patterns.
            </p>
            <div className="flex gap-4">
              {[
                { icon: Linkedin, href: "https://www.linkedin.com/company/hirewithgreentick", label: "LinkedIn" },
                { icon: Twitter, href: "#", label: "Twitter" },
                { icon: Github, href: "#", label: "GitHub" },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:bg-teal-500/20 hover:border-teal-500/50 hover:text-teal-400 transition-all group"
                  aria-label={social.label}
                  title={`Follow GreenTick on ${social.label}`}
                >
                  <social.icon className="w-5 h-5 transition-transform group-hover:scale-110" />
                </a>
              ))}
            </div>
          </div>

          {/* Solutions Column */}
          <div className="lg:col-span-2">
            <h2 className="text-white font-bold mb-8 uppercase tracking-widest text-[10px] italic underline decoration-teal-500/20 underline-offset-4">Verticals</h2>
            <ul className="space-y-4">
              {[
                { label: "For Corporates", to: "/for-corporates" },
                { label: "For Institutes", to: "/for-institutes" },
                { label: "For Individuals", to: "/for-individuals" },
                { label: "Integrations", to: "/integrations" },
              ].map((link, i) => (
                <li key={i}>
                  <Link to={link.to} className="text-slate-400 hover:text-teal-400 transition-colors text-sm font-medium flex items-center group">
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Research Lab Column */}
          <div className="lg:col-span-2">
            <h2 className="text-white font-bold mb-8 uppercase tracking-widest text-[10px] italic underline decoration-teal-500/20 underline-offset-4">Research Lab</h2>
            <ul className="space-y-4">
              {[
                { label: "Technical Blog", to: "/blog" },
                { label: "Research Papers", to: "/research" },
                { label: "BSA Methodology", to: "/research/psychometrics-ai-interviews" },
                { label: "Archive Index", to: "/research" },
              ].map((link, i) => (
                <li key={i}>
                  <Link to={link.to} className="text-slate-400 hover:text-teal-400 transition-colors text-sm font-medium flex items-center group">
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div className="lg:col-span-2">
            <h2 className="text-white font-bold mb-8 uppercase tracking-widest text-[10px] italic underline decoration-teal-500/20 underline-offset-4">Company</h2>
            <ul className="space-y-4">
              {[
                { label: "About Us", to: "/about" },
                { label: "Careers", to: "/careers" },
                { label: "Contact Us", to: "/contact" },
                { label: "Privacy Core", to: "/privacy_policy" },
              ].map((link, i) => (
                <li key={i}>
                  <Link to={link.to} className="text-slate-400 hover:text-teal-400 transition-colors text-sm font-medium">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col md:flex-row items-center gap-4 text-xs font-medium text-slate-400">
            <p>© {currentYear} GreenTick AI — Unified Hiring Intelligence.</p>
            <div className="hidden md:block w-1.5 h-1.5 rounded-full bg-slate-800"></div>
            <div className="flex gap-6">
              <Link to="/privacy_policy" className="hover:text-teal-400 transition-colors italic">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-teal-400 transition-colors italic">Terms of Service</Link>
            </div>
          </div>
          
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
             <div className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></div>
             <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Archive Synced: 99.9% Uptime</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

